import { useMemo, Fragment, ReactNode } from 'react';

import { FormFieldType } from 'types';
import { isStaticFormField } from 'utils/static-form-field';

import { FormText } from './text';
import { FormHeading } from './heading';
import { FormInputField } from './input';
import { FormRadioField } from './radio';
import { FormSelectField } from './select';
import { OutcomesField } from './outcomes';
import { FormCheckboxField } from './checkbox';
import { FieldArrayField } from './field-array';
import { DynamicFormFieldsProps } from './types';
import { FormScaleRatingField } from './scale-rating';
import { OutcomeFieldsField } from './outcome-fields';

export const DynamicFormFields = ({
  fields,
  container,
  hideLabels,
  expandableInput,
  fieldArrayProps,
  outcomesStateById,
  inputLabelOutside,
  isSelectSearchable,
  outcomeFieldsSourceStateById,
}: DynamicFormFieldsProps) => {
  const items = useMemo(() => {
    // Static content bypasses question wrappers, keeping their indexes contiguous.
    let wrappedFieldIndex = 0;
    return fields.map((field, index) => {
      let fieldNode: ReactNode;
      const props = { ...field };
      if (hideLabels && !isStaticFormField(props.type)) {
        props.label = '';
      }
      switch (props.type) {
        case FormFieldType.INPUT:
          fieldNode = (
            <FormInputField
              key={props.id}
              expandable={expandableInput}
              labelOutside={inputLabelOutside}
              {...props}
            />
          );
          break;
        case FormFieldType.SELECT:
          fieldNode = (
            <FormSelectField
              key={props.id}
              labelOutside={inputLabelOutside}
              isSearchable={isSelectSearchable}
              {...props}
            />
          );
          break;
        case FormFieldType.RADIO:
          fieldNode = <FormRadioField key={props.id} {...props} />;
          break;
        case FormFieldType.CHECKBOX:
          fieldNode = <FormCheckboxField key={props.id} {...props} />;
          break;
        case FormFieldType.SCALE_RATING:
          fieldNode = <FormScaleRatingField key={props.id} {...props} />;
          break;
        case FormFieldType.FIELD_ARRAY:
          fieldNode = (
            <FieldArrayField
              key={props.id}
              {...props}
              fieldArrayProps={fieldArrayProps}
            />
          );
          break;
        case FormFieldType.TEXT:
          fieldNode = <FormText key={props.id} {...props} />;
          break;
        case FormFieldType.HEADING:
          fieldNode = <FormHeading key={props.id} {...props} />;
          break;
        case FormFieldType.OUTCOMES:
          fieldNode = (
            <OutcomesField
              key={props.id}
              {...props}
              {...outcomesStateById?.[props.id]}
            />
          );
          break;
        case FormFieldType.OUTCOME_FIELDS:
          fieldNode = (
            <OutcomeFieldsField
              key={props.id}
              {...props}
              fieldArrayProps={fieldArrayProps}
              sourceValue={outcomeFieldsSourceStateById?.[props.id]?.value}
              sourceLoading={
                outcomeFieldsSourceStateById?.[props.id]?.isLoading
              }
            />
          );
          break;
        default:
          fieldNode = <Fragment key={index} />;
          break;
      }
      if (isStaticFormField(field.type)) {
        return fieldNode;
      }

      const containerIndex = wrappedFieldIndex;
      wrappedFieldIndex += 1;
      return container
        ? container(field, fieldNode, containerIndex)
        : fieldNode;
    });
  }, [
    fields,
    container,
    hideLabels,
    expandableInput,
    isSelectSearchable,
    inputLabelOutside,
    fieldArrayProps,
    outcomesStateById,
    outcomeFieldsSourceStateById,
  ]);

  return <>{items}</>;
};
